import * as React from 'react'
import { TextField } from '../ui/TextField'
import { Button, ButtonText } from '../ui/button'
import { useController, useForm } from 'react-hook-form'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import useGetVaults from '@/services/api/vault/useGetVaults'
import useCreateVault from '@/services/api/vault/useCreateVault'
import { DateAfter } from 'react-day-picker'
import { unformatNumeral } from 'cleave-zen'
import AmountInput from '@/components/AmountInput'
import NonModalWindow from '@/components/NonModalWindow'
import ConfirmationModal from '../ConfirmationModal'
import { Label } from '../ui/Label'
import FieldStatusMessage from '../ui/FieldStatusMessage'
import Field from '../ui/Field'
import useCloseToOrigin from '@/hooks/useCloseToOrigin'
import SimpleComboBox from '../SimpleComboBox'
import { Description } from '../ui/Description'

export interface VaultFormValues {
  name: string
  description: string
  initial_balance: string
  balance_as_of: string
  vault_group: {
    id: string
    name: string
    description: string
  }
}

export default function AddVaultForm() {
  const closeToOrigin = useCloseToOrigin()
  const [newGroupIsOpen, setNewGroupIsOpen] = useState(false)
  const [isFocusedName, setIsFocusedName] = useState(false)
  const [isFocusedDescription, setIsFocusedNameDescription] = useState(false)
  const [isFocusedGroupName, setIsFocusedGroupName] = useState(false)
  const [isFocusedGroupDescription, setIsFocusedNameGroupDescription] =
    useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    setValue,
  } = useForm<VaultFormValues>({
    defaultValues: {
      balance_as_of: `${format(new Date(), 'yyyy-MM-dd')}`,
      name: '',
      description: '',
      vault_group: null,
      initial_balance: '0.00',
    },
  })

  const { data: dataorg } = useGetVaults()

  const vaultGroups = dataorg.filter(
    (vault) => vault.entity_type === 'vault_group' && vault.id !== -1,
  )

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  React.useEffect(() => {
    setValue('name', name)
  }, [name])

  const handleNameChange = (event) => {
    const value = event.target.value
    if (value.length <= 30) {
      setName(value)
    }
  }

  React.useEffect(() => {
    setValue('description', description)
  }, [description])

  const handleDescriptionChange = (event) => {
    const value = event.target.value
    if (value.length <= 40) {
      setDescription(value)
    }
  }

  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')

  React.useEffect(() => {
    setValue('vault_group.name', groupName)
  }, [groupName])

  const handleGroupNameChange = (event) => {
    const value = event.target.value
    if (value.length <= 30) {
      setGroupName(value)
    }
  }

  React.useEffect(() => {
    setValue('vault_group.description', groupDescription)
  }, [groupDescription])

  const handleGroupDescriptionChange = (event) => {
    const value = event.target.value
    if (value.length <= 40) {
      setGroupDescription(value)
    }
  }

  const { field } = useController({
    control: control,
    name: 'balance_as_of',
    shouldUnregister: true,
  })

  const { field: groupField } = useController({
    control: control,
    name: 'vault_group',
    shouldUnregister: true,
  })

  const { field: amountField } = useController({
    control: control,
    name: 'initial_balance',
    shouldUnregister: true,
    rules: { required: 'Please fill out this field' },
  })

  const { createVault } = useCreateVault({ setError: setError })

  useEffect(() => {
    if (name || description || amountField.value || groupField.value) {
      setIsFormDirty(true)
    } else {
      setIsFormDirty(false)
    }
  }, [name, description, amountField.value, groupField.value])

  const handleClose = () => {
    if (isFormDirty) {
      setOpenConfirmation(true)
    } else {
      closeToOrigin()
    }
  }

  const onSubmit = async (data) => {
    const { initial_balance } = data
    const amountNum = parseFloat(unformatNumeral(initial_balance))
    await createVault({ ...data, initial_balance: amountNum })
    closeToOrigin()
  }

  const afterMatcher: DateAfter = { after: new Date() }

  return (
    <>
      <ConfirmationModal
        type="Add New Vault"
        open={openConfirmation}
        setOpen={setOpenConfirmation}
      />
      <form
        noValidate
        className=" flex flex-col"
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(onSubmit)
        }}
      >
        <NonModalWindow
          onSubmit={handleSubmit(onSubmit)}
          title="Add New Vault"
          onClose={() => handleClose()}
          isDirty={isFormDirty}
        >
          <NonModalWindow.Body className="min-h-[78vh]">
            <p className="mb-3 text-sm text-txt-secondary">General</p>
            <div className="mb-3">
              <TextField
                status={errors?.name ? 'error' : null}
                statusText={errors.name?.message}
              >
                <TextField.Label className="flex justify-between text-sm">
                  Vault Name
                  <span
                    className={`text-txt-secondary ${isFocusedName ? '' : 'hidden'}`}
                  >
                    {name.length}/30
                  </span>
                </TextField.Label>
                <TextField.Input
                  maxLength={30}
                  placeholder="e.g., Bank Name"
                  {...register('name', {
                    required: 'Please fill out this field',
                    value: name,
                    onChange: handleNameChange,
                  })}
                  onFocus={() => setIsFocusedName(true)}
                  onBlur={() => setIsFocusedName(false)}
                />
              </TextField>
            </div>
            <div className="mb-4">
              <TextField>
                <TextField.Label className="flex  justify-between text-sm">
                  Description
                  <span
                    className={`text-txt-secondary ${isFocusedDescription ? '' : 'hidden'}`}
                  >
                    {description.length}/40
                  </span>
                </TextField.Label>
                <TextField.Input
                  maxLength={40}
                  placeholder="e.g., Main Account"
                  {...register('description', {
                    value: description,
                    onChange: handleDescriptionChange,
                  })}
                  onFocus={() => setIsFocusedNameDescription(true)}
                  onBlur={() => setIsFocusedNameDescription(false)}
                />
              </TextField>
            </div>

            <p className="mb-3 text-sm text-txt-secondary">Balance</p>
            <div className="mb-3">
              <Field
                status={errors.initial_balance ? 'error' : undefined}
                statusText={errors.initial_balance?.message.toString()}
              >
                <Label>Amount</Label>
                <AmountInput
                  placeholder="0.00"
                  error={errors.initial_balance?.message}
                  controllerField={amountField}
                />
                <FieldStatusMessage />
              </Field>
            </div>
            <div className="mb-4">
              <TextField>
                <TextField.Label className="text-sm">
                  Date as of
                </TextField.Label>
                <TextField.DatePicker
                  value={format(field.value, 'yyyy-MM-dd')}
                  onSelect={field.onChange}
                  disabled={afterMatcher}
                  {...register('balance_as_of')}
                />
              </TextField>
            </div>

            <p className="mb-3 text-sm text-txt-secondary">
              Add to group(optional)
            </p>
            <div className="mb-3">
              <TextField>
                <TextField.Label className="text-sm">Group</TextField.Label>
                <SimpleComboBox
                  align="end"
                  placeholder="Select a group"
                  items={vaultGroups}
                  selected={groupField.value}
                  setSelected={(val) => {
                    setNewGroupIsOpen(false)
                    groupField.onChange(val)
                  }}
                  getOptionLabel={(item) => (
                    <div>
                      <p>{item.name}</p>
                      <Description>{item.description}</Description>
                    </div>
                  )}
                  getSelectedLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  getFixedOptionLabel={() => 'Create New Group'}
                  fixedOption={
                    <div
                      onClick={() => {
                        setNewGroupIsOpen(true)

                        groupField.onChange(null)
                      }}
                    >
                      Create New Group
                    </div>
                  }
                />
              </TextField>
            </div>
            {newGroupIsOpen && (
              <>
                <div className="mb-3">
                  <TextField>
                    <TextField.Label className="flex  justify-between text-sm">
                      Group Name
                      <span
                        className={`text-txt-secondary ${isFocusedGroupName ? '' : 'hidden'}`}
                      >
                        {groupName.length}/30
                      </span>
                    </TextField.Label>
                    <TextField.Input
                      maxLength={30}
                      placeholder="e.g., Main Vault Group"
                      {...register('vault_group.name', {
                        required: 'Please fill out this field',
                        value: groupName,
                        onChange: handleGroupNameChange,
                      })}
                      onFocus={() => setIsFocusedGroupName(true)}
                      onBlur={() => setIsFocusedGroupName(false)}
                    />
                  </TextField>
                </div>
                <div className="mb-4">
                  <TextField>
                    <TextField.Label className="flex  justify-between text-sm">
                      Group Description
                      <span
                        className={`text-txt-secondary ${isFocusedGroupDescription ? '' : 'hidden'}`}
                      >
                        {groupDescription.length}/40
                      </span>
                    </TextField.Label>
                    <TextField.Input
                      placeholder="e.g., For main company accounts"
                      {...register('vault_group.description', {
                        value: groupDescription,
                        onChange: handleGroupDescriptionChange,
                      })}
                      onFocus={() => setIsFocusedNameGroupDescription(true)}
                      onBlur={() => setIsFocusedNameGroupDescription(false)}
                    />
                  </TextField>
                </div>
              </>
            )}
          </NonModalWindow.Body>
          <NonModalWindow.Footer>
            <Button
              variant="secondary"
              className="mr-2"
              onClick={() => {
                handleClose()
              }}
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
            <Button type="submit" onClick={handleSubmit(onSubmit)}>
              <ButtonText>Add Vault</ButtonText>
            </Button>
          </NonModalWindow.Footer>
        </NonModalWindow>
      </form>
    </>
  )
}
